/**
 * usePermissions Hook
 *
 * Provides easy access to user permissions throughout the app.
 * Includes helper functions for checking specific permissions.
 * Falls back to role-based permissions for owners/admins.
 */

import { useAuth } from '../contexts/AuthContext'

export const usePermissions = () => {
  const { user, permissions } = useAuth()

  // If user is owner or admin, they have all permissions
  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin'

  // Helper function to check a specific permission
  const hasPermission = (permissionName) => {
    if (!user) return false

    // Owners and admins always have all permissions
    if (isOwnerOrAdmin) return true

    // Check permission from database
    return permissions?.[permissionName] ?? false
  }

  // Convenience functions for common permission checks
  const canCreateAssessments = () => hasPermission('can_create_assessments')
  const canViewAllAssessments = () => hasPermission('can_view_all_assessments')
  const canViewOwnAssessmentsOnly = () => hasPermission('can_view_own_assessments_only')
  const canEditAssessments = () => hasPermission('can_edit_assessments')
  const canDeleteAssessments = () => hasPermission('can_delete_assessments')
  const canShareAssessments = () => hasPermission('can_share_assessments')

  const canViewCustomers = () => hasPermission('can_view_customers')
  const canCreateCustomers = () => hasPermission('can_create_customers')
  const canEditCustomers = () => hasPermission('can_edit_customers')
  const canDeleteCustomers = () => hasPermission('can_delete_customers')

  const canViewTeam = () => hasPermission('can_view_team')
  const canInviteUsers = () => hasPermission('can_invite_users')
  const canManageTeam = () => hasPermission('can_manage_team')
  const canAccessSettings = () => hasPermission('can_access_settings')
  const canManageBilling = () => hasPermission('can_manage_billing')

  const canViewDashboard = () => hasPermission('can_view_dashboard')
  const canViewAnalytics = () => hasPermission('can_view_analytics')
  const canExportReports = () => hasPermission('can_export_reports')

  // Get access mode
  const getAccessMode = () => {
    if (isOwnerOrAdmin) return 'office' // Owners/admins always use office mode
    return permissions?.access_mode || 'office'
  }

  const isFieldMode = () => getAccessMode() === 'field'
  const isOfficeMode = () => getAccessMode() === 'office'

  // Get permission summary (useful for UI display)
  const getPermissionSummary = () => {
    if (isOwnerOrAdmin) {
      return {
        assessments: 'Full Access',
        customers: 'Full Access',
        team: 'Full Access',
        settings: 'Full Access',
        reporting: 'Full Access'
      }
    }

    const summary = {}

    // Assessments
    if (canViewOwnAssessmentsOnly()) {
      summary.assessments = 'Own Only'
    } else if (canEditAssessments()) {
      summary.assessments = 'Full Access'
    } else if (canViewAllAssessments()) {
      summary.assessments = 'View All'
    } else if (canCreateAssessments()) {
      summary.assessments = 'Create Only'
    } else {
      summary.assessments = 'No Access'
    }

    // Customers
    if (canEditCustomers() || canDeleteCustomers()) {
      summary.customers = 'Full Access'
    } else if (canViewCustomers() && canCreateCustomers()) {
      summary.customers = 'View & Create'
    } else if (canViewCustomers()) {
      summary.customers = 'View Only'
    } else if (canCreateCustomers()) {
      summary.customers = 'Create Only'
    } else {
      summary.customers = 'No Access'
    }

    // Team
    if (canManageTeam()) {
      summary.team = 'Full Access'
    } else if (canViewTeam()) {
      summary.team = 'View Only'
    } else {
      summary.team = 'No Access'
    }

    // Settings
    if (canManageBilling() || canAccessSettings()) {
      summary.settings = 'Full Access'
    } else {
      summary.settings = 'No Access'
    }

    // Reporting
    if (canExportReports()) {
      summary.reporting = 'Full Access'
    } else if (canViewAnalytics()) {
      summary.reporting = 'Analytics'
    } else if (canViewDashboard()) {
      summary.reporting = 'Dashboard Only'
    } else {
      summary.reporting = 'No Access'
    }

    return summary
  }

  return {
    // Raw permissions object
    permissions,

    // Permission checks
    hasPermission,
    isOwnerOrAdmin,

    // Assessment permissions
    canCreateAssessments,
    canViewAllAssessments,
    canViewOwnAssessmentsOnly,
    canEditAssessments,
    canDeleteAssessments,
    canShareAssessments,

    // Customer permissions
    canViewCustomers,
    canCreateCustomers,
    canEditCustomers,
    canDeleteCustomers,

    // Team & Settings permissions
    canViewTeam,
    canInviteUsers,
    canManageTeam,
    canAccessSettings,
    canManageBilling,

    // Reporting permissions
    canViewDashboard,
    canViewAnalytics,
    canExportReports,

    // Access mode
    getAccessMode,
    isFieldMode,
    isOfficeMode,

    // Utility
    getPermissionSummary
  }
}
